import secureApi from '../../utils/secureApi';

export interface TeamStanding {
  team: {
    _id: string;
    name: string;
    shortName: string;
    logo?: string | null;
  };
  played: number;
  won: number;
  lost: number;
  drawn: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  isQualified?: boolean;
}

export interface GroupStanding {
  _id?: string;
  groupId?: string;
  groupName: string;
  standings: TeamStanding[];
}

export interface Standing {
  _id: string;
  team: {
    _id: string;
    name: string;
    shortName: string;
  };
  ageCategory: {
    _id: string;
    ageCategory: string;
  };
  tournament: {
    _id: string;
    name: string;
    shortName: string;
  };
  group?: string;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface StandingsResponse {
  paginatedResults: Standing[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface TeamStandingsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: TeamStanding[] | GroupStanding[];
}

export interface StandingsFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  tournamentId?: string;
  ageCategory?: string;
  group?: string;
}

export interface StandingPayload {
  tournament: string;
  team: string;
  ageCategory: string;
  group: string;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  position: number;
}

const requiredSport = (sport?: string): string => {
  if (!sport?.trim()) {
    throw new Error("A sport is required for standings requests.");
  }
  return sport;
};

// Get all standings for an age category
export const getAllStandings = async (
  ageCategoryId: string,
  filters: StandingsFilters = {},
  sport: string
): Promise<StandingsResponse> => {
  const params = new URLSearchParams();
  params.append('sport', sport);
  
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.ageCategory) params.append('ageCategory', filters.ageCategory);
  if (filters.group) params.append('group', filters.group);

  const response = await secureApi.get<any>(
    `/api/v2/tournaments/admin/standings/get-all-standings/${ageCategoryId}?${params.toString()}`
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data && (response.data.paginatedResults || response.data.totalCount !== undefined)) {
    return response.data;
  } else if (response && (response.paginatedResults || response.totalCount !== undefined)) {
    return response;
  }
  return response;
};

// Get team standings for an age category (new API structure)
export const getTeamStandings = async (
  ageCategoryId: string,
  sport: string,
  tournamentSeasonId?: string
): Promise<TeamStanding[] | GroupStanding[]> => {
  const params = new URLSearchParams();
  params.append('sport', sport);
  if (tournamentSeasonId) {
    params.append('tournamentSeason', tournamentSeasonId);
  }
  const url = `/api/v2/tournaments/admin/standings/get-all-standings/${ageCategoryId}?${params.toString()}`;
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  // The API may return data directly as an array or wrapped in a data property
  if (response && response.data) {
    // If data is an array, return it directly
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // If data is an object with an array property, return that
    return response.data;
  } else if (Array.isArray(response)) {
    // Response is already an array
    return response;
  }
  return response;
};

export const recomputeStandings = async (ageCategoryId: string): Promise<any> =>
  secureApi.post(`/api/v2/tournaments/multi/updateAllStandings/${encodeURIComponent(ageCategoryId)}`, {});

// Get standings by ID
export const getStandingById = async (standingId: string, sport?: string): Promise<{ data: Standing }> => {
  let url = `/api/v2/tournaments/admin/standings/get-standing/${standingId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.get<{ data: Standing }>(url);
};

// Add teams to standings
export interface AddTeamsToStandingsRequest {
  tournamentCategoryId: string;
  groupName?: string;
  teams: Array<{
    id: string;
    name: string;
  }>;
}

export const addTeamsToStandings = async (
  data: AddTeamsToStandingsRequest,
  sport?: string
): Promise<any> => {
  const sportValue = requiredSport(sport);
  const url = `/api/v2/tournaments/admin/standings/add?sport=${encodeURIComponent(sportValue)}`;
  return await secureApi.post<any>(url, data);
};

// Add new standing
export const addStanding = async (standingData: StandingPayload, sport?: string): Promise<Standing> => {
  const sportValue = requiredSport(sport);
  const url = `/api/v2/tournaments/admin/standings/add-standing?sport=${encodeURIComponent(sportValue)}`;
  return await secureApi.post<Standing>(url, standingData);
};

// Update standing
export const updateStanding = async (standingId: string, standingData: StandingPayload, sport?: string): Promise<Standing> => {
  const sportValue = requiredSport(sport);
  const url = `/api/v2/tournaments/admin/standings/update-standing/${standingId}?sport=${encodeURIComponent(sportValue)}`;
  return await secureApi.put<Standing>(url, standingData);
};

// Delete standing
export const deleteStanding = async (standingId: string, sport?: string): Promise<void> => {
  const sportValue = requiredSport(sport);
  const url = `/api/v2/tournaments/admin/standings/delete-standing/${standingId}?sport=${encodeURIComponent(sportValue)}`;
  await secureApi.delete(url);
};

// Get standings by age category
export const getStandingsByAgeCategory = async (
  tournamentId: string,
  ageCategory: string,
  group?: string
): Promise<Standing[]> => {
  const params = new URLSearchParams();
  params.append('ageCategory', ageCategory);
  if (group) params.append('group', group);

  return await secureApi.get<Standing[]>(
    `/api/v2/tournaments/admin/standings/get-standings-by-age-category/${tournamentId}?${params.toString()}`
  );
};

// Update standings after match result
export const updateStandingsAfterMatch = async (
  tournamentId: string,
  matchData: {
    teamA: string;
    teamB: string;
    teamAScore: number;
    teamBScore: number;
    ageCategory: string;
    group?: string;
  }
): Promise<void> => {
  let url = `/api/v2/tournaments/admin/standings/update-after-match/${tournamentId}`;
  await secureApi.post(url, matchData);
};

// Edit standings for a specific team
export interface EditStandingsRequest {
  categoryId: string;
  teamId: string;
  updates: Record<string, number | boolean>;
}

export const editStandings = async (data: EditStandingsRequest, sport: string): Promise<any> => {
  const url = `/api/v2/tournaments/admin/standings/edit?sport=${encodeURIComponent(sport)}`;
  return await secureApi.post<any>(url, data);
};

// Remove team from standings
export interface RemoveTeamFromStandingsRequest {
  tournamentId: string;
  ageCategoryId: string;
  teamId: string;
  groupName?: string; // Optional for group standings
  groupId?: string; // Optional for group standings
}

export const removeTeamFromStandings = async (data: RemoveTeamFromStandingsRequest, sport: string): Promise<any> => {
  let url = `/api/v2/tournaments/admin/${data.ageCategoryId}/standing/delete/${data.teamId}`;
  
  if (data.groupName) {
    url += `/${encodeURIComponent(data.groupName)}`;
  }
  if (data.groupId) {
    url += `/${encodeURIComponent(data.groupId)}`;
  }
  
  url += `?sport=${encodeURIComponent(sport)}`;


  // Use POST request with empty JSON object as per curl command
  return await secureApi.post<any>(url, {});
};

// Get single team standing
export const getTeamStanding = async (
  ageCategoryId: string, 
  teamId: string, 
  groupName?: string,
  sport?: string
): Promise<any> => {
  const sportValue = requiredSport(sport);
  let url = `/api/v2/tournaments/admin/${ageCategoryId}/standing/${teamId}`;
  
  if (groupName) {
    url += `/${encodeURIComponent(groupName)}`;
  }
  
  url += `?sport=${encodeURIComponent(sportValue)}`;

  return await secureApi.get<any>(url);
};

// Edit teams in standings (for bulk operations)
export interface EditTeamsInStandingsRequest {
  tournamentCategoryId: string;
  teams: Array<{
    teamId: string;
    played: number;
    won: number;
    lost: number;
    drawn: number;
    goalsFor: number;
    goalsAgainst: number;
    points: number;
    isQualified: boolean;
  }>;
  groupId?: string; // Optional for group standings
}

export const editTeamsInStandings = async (data: EditTeamsInStandingsRequest, sport?: string): Promise<any> => {
  let url = '/api/v2/tournaments/admin/standings/edit-teams';
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, data);
};
